import { Component, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ApiService, AuthService } from '@homeleite/arch';
import { jwtDecode } from 'jwt-decode';
import { lastValueFrom, Subscription } from 'rxjs';
import { ALL_ASSOCIATIONS, associationView, setAssociationView } from './association-view';
import {
    API_ENVIRONMENTS,
    ApiEnvKey,
    currentApiEnvKey,
    switchApiEnvironment
} from './api-environments';
import { APP_VERSION } from './app-version';

interface AssociationOption {
    id: string;
    name: string;
}

@Component({
    selector: 'homeleite-env-banner',
    template: `
        <div class="env-stack">
        <div class="env-banner" [class.prod]="key === 'production'" role="status">
            <strong>{{ key === 'production' ? '⚠ PRODUÇÃO' : 'HOMOLOGAÇÃO' }}</strong>
            <span class="sep">|</span>
            <label>
                Ambiente:
                <select [value]="key" (change)="onChange($event)" aria-label="Ambiente da API">
                    <option value="production">Produção</option>
                    <option value="homologation">Homologação</option>
                </select>
            </label>
            @if (key === 'production') {
            <span class="env-hint">API do administrator-parleite</span>
            }
            @if (associations.length) {
            <span class="sep">|</span>
            <label>
                Associação:
                <select
                    class="assoc"
                    [class.away]="markedAway"
                    (change)="onAssociation($event)"
                    aria-label="Associação"
                    title="Todas lista as associações. Uma associação mostra só os clientes dela."
                >
                    @if (key !== 'production') {
                    <option [value]="allValue" [selected]="showAll">Todas</option>
                    }
                    @for (association of associations; track association.id) {
                    <option [value]="association.id" [selected]="!showAll && association.id === associationId">
                        {{ labelOf(association) }}
                    </option>
                    }
                </select>
            </label>
            }
            <span class="sep">|</span>
            <span class="env-ver" title="Versão do Admin HML">v{{ version }}</span>
        </div>
        @if (markedAway) {
        <div class="away-bar" role="status">
            <span class="mark">Outra associação</span>
            <span class="name">{{ currentLabel }}</span>
        </div>
        }
        @if (pending) {
        <div
            class="switch-mask"
            role="dialog"
            aria-modal="true"
            aria-labelledby="switch-title"
            (keydown.escape)="cancelSwitch()"
        >
            <div class="switch-card">
                <p id="switch-title" class="kicker">Trocar associação</p>
                <div class="swap">
                    <div>
                        <span class="k">De</span>
                        <strong>{{ currentLabel }}</strong>
                    </div>
                    <span class="arrow" aria-hidden="true">→</span>
                    <div [class.dest]="pending.id !== 'APCBRH'">
                        <span class="k">Para</span>
                        <strong>{{ pending.label }}</strong>
                    </div>
                </div>
                <p class="switch-copy">{{ pendingCopy }}</p>
                <div class="switch-actions">
                    <button type="button" (click)="cancelSwitch()" [disabled]="switching">
                        Continuar aqui
                    </button>
                    <button type="button" class="go" (click)="confirmSwitch()" [disabled]="switching">
                        Trocar para {{ pending.id }}
                    </button>
                </div>
            </div>
        </div>
        }
        </div>
    `,
    styles: [
        `
            .env-stack {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 3000;
            }
            .env-banner {
                height: 28px;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                font: 600 12px/1 var(--hl-font, Roboto, sans-serif);
                background: #ffb300;
                color: #1a1f1c;
            }
            .away-bar {
                height: 28px;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 10px;
                padding: 0 16px;
                background: #1a5b32;
                color: #fff;
                font: 600 13px/1 var(--hl-font, Roboto, sans-serif);
            }
            .mark {
                flex: 0 0 auto;
                background: #fff;
                color: #1a5b32;
                border-radius: 999px;
                padding: 4px 8px;
                font-size: 11px;
                letter-spacing: 0.04em;
                text-transform: uppercase;
            }
            .name {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .switch-mask {
                position: fixed;
                inset: 0;
                z-index: 4000;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 24px;
                background: rgba(26, 31, 28, 0.45);
            }
            .switch-card {
                width: min(440px, 100%);
                background: #fff;
                color: #1a1f1c;
                border-radius: 10px;
                padding: 20px;
                box-shadow: 0 12px 40px rgba(26, 31, 28, 0.2);
            }
            .kicker {
                margin: 0 0 12px;
                color: #1a5b32;
                font: 600 12px/1 var(--hl-font, Roboto, sans-serif);
                letter-spacing: 0.06em;
                text-transform: uppercase;
            }
            .swap {
                display: flex;
                align-items: center;
                gap: 12px;
            }
            .swap > div {
                flex: 1;
                min-width: 0;
                background: #f6f8f6;
                border-radius: 8px;
                padding: 10px 12px;
            }
            .swap > div.dest {
                background: #eef4ef;
                outline: 2px solid #1a5b32;
            }
            .k {
                display: block;
                margin-bottom: 4px;
                color: #5c6b60;
                font-size: 11px;
                text-transform: uppercase;
            }
            .swap strong {
                display: block;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-size: 14px;
            }
            .arrow {
                color: #1a5b32;
                font-size: 20px;
            }
            .switch-copy {
                margin: 14px 0;
                color: #1a1f1c;
                font: 400 13px/1.4 var(--hl-font, Roboto, sans-serif);
            }
            .switch-actions {
                display: flex;
                justify-content: flex-end;
                gap: 8px;
            }
            .switch-actions button {
                height: 36px;
                padding: 0 14px;
                border-radius: 8px;
                border: 1px solid #1a5b32;
                background: #fff;
                color: #1a5b32;
                font: 600 13px/1 var(--hl-font, Roboto, sans-serif);
                cursor: pointer;
            }
            .switch-actions button.go {
                background: #1a5b32;
                color: #fff;
            }
            .switch-actions button:disabled {
                opacity: 0.6;
                cursor: default;
            }
            .env-banner.prod {
                background: var(--hl-env-prod, #c62828);
                color: #fff;
            }
            .env-hint {
                opacity: 0.9;
                font-size: 11px;
            }
            .env-ver {
                font-size: 11px;
                font-weight: 700;
                font-variant-numeric: tabular-nums;
                opacity: 0.95;
            }
            select {
                font: inherit;
                max-width: 140px;
            }
            select.assoc {
                max-width: 260px;
            }
            select.assoc.away {
                max-width: 280px;
                height: 22px;
                border: 2px solid #1a5b32;
                border-radius: 6px;
                background: #eef4ef;
                color: #1a1f1c;
                font-weight: 700;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EnvBannerComponent implements OnInit, OnDestroy {
    key: ApiEnvKey = currentApiEnvKey();
    readonly version = APP_VERSION;
    readonly allValue = ALL_ASSOCIATIONS;
    associations: AssociationOption[] = [];
    associationId = '';
    showAll = associationView() === 'all';
    pending: { id: string; label: string } | null = null;
    switching = false;
    private tokenSub?: Subscription;
    private readonly onView = () => {
        this.zone.run(() => {
            this.showAll = associationView() === 'all';
            this.syncBannerHeight();
        });
    };

    constructor(
        private api: ApiService,
        private auth: AuthService,
        private zone: NgZone
    ) {}

    ngOnInit() {
        this.syncBannerHeight();
        window.addEventListener('hl-association-view', this.onView);
        this.tokenSub = this.auth.$currentToken.subscribe(() => {
            void this.refreshAssociations();
        });
    }

    ngOnDestroy() {
        this.tokenSub?.unsubscribe();
        window.removeEventListener('hl-association-view', this.onView);
        document.documentElement.style.removeProperty('--env-banner-h');
    }

    get selectedAssociation(): string {
        return this.showAll ? this.allValue : this.associationId;
    }

    get markedAway(): boolean {
        return !!this.associationId && this.associationId !== 'APCBRH';
    }

    get currentLabel(): string {
        const association = this.associations.find((item) => item.id === this.associationId);
        return association ? this.labelOf(association) : this.associationId || 'APCBRH';
    }

    get pendingCopy(): string {
        if (this.pending?.id === 'APCBRH') {
            return 'O painel volta a mostrar só os clientes da APCBRH.';
        }
        return 'O painel passa a mostrar só os clientes da associação de destino.';
    }

    labelOf(association: AssociationOption): string {
        return association.name && association.name !== association.id
            ? `${association.id} — ${association.name}`
            : association.id;
    }

    async onAssociation(event: Event) {
        const select = event.target as HTMLSelectElement;
        const next = select.value;
        if (!next || next === this.selectedAssociation) {
            return;
        }
        if (next === this.allValue) {
            setAssociationView('all');
            this.showAll = true;
            if (!location.pathname.includes('integration-settings')) {
                location.assign('/integration-settings');
            }
            return;
        }
        const association = this.associations.find((item) => item.id === next);
        const label = association ? this.labelOf(association) : next;
        if (next === this.currentTenantId()) {
            setAssociationView('one');
            this.showAll = false;
            this.syncBannerHeight();
            return;
        }
        select.value = this.selectedAssociation;
        this.pending = { id: next, label };
    }

    cancelSwitch(): void {
        this.pending = null;
        this.switching = false;
    }

    async confirmSwitch(): Promise<void> {
        if (!this.pending || this.switching) {
            return;
        }
        const tenantId = this.pending.id;
        this.switching = true;
        const entered = await this.useAssociation(tenantId);
        this.switching = false;
        if (entered) {
            this.pending = null;
        }
    }

    private async refreshAssociations() {
        if (!this.isAdmin()) {
            this.associations = [];
            this.associationId = '';
            return;
        }
        try {
            const list = await lastValueFrom(
                this.api.root.path('/public/tenants').getJson<AssociationOption[]>()
            );
            const current = this.currentTenantId();
            const loaded = Array.isArray(list) ? list : [];
            this.associations =
                current && !loaded.some((item) => item.id === current)
                    ? [{ id: current, name: current }, ...loaded]
                    : loaded;
            this.associationId = current && this.associations.some((item) => item.id === current)
                ? current
                : this.associations[0]?.id ?? '';
            this.syncBannerHeight();
        } catch {
            this.associations = [];
            this.syncBannerHeight();
        }
    }

    private syncBannerHeight(): void {
        document.documentElement.style.setProperty('--env-banner-h', this.markedAway ? '56px' : '28px');
    }

    private async useAssociation(tenantId: string): Promise<boolean> {
        try {
            const result = await lastValueFrom(
                this.api.root.path('/association-token').postJson<{ token: string }>({ tenantId })
            );
            if (!result?.token) {
                throw new Error('sem token');
            }
            setAssociationView('one');
            this.auth.publishLogin(result.token);
            location.reload();
            return true;
        } catch {
            alert('Não foi possível entrar nessa associação.');
            return false;
        }
    }

    private isAdmin(): boolean {
        const token = this.auth.currentToken;
        if (!token) {
            return false;
        }
        try {
            const decoded = jwtDecode<{ auth?: string }>(token);
            return (decoded.auth ?? '').split(',').includes('ROLE_ADMIN');
        } catch {
            return false;
        }
    }

    private currentTenantId(): string {
        try {
            return jwtDecode<{ tenantId?: string }>(this.auth.currentToken).tenantId ?? '';
        } catch {
            return '';
        }
    }

    onChange(event: Event) {
        const select = event.target as HTMLSelectElement;
        const next = select.value as ApiEnvKey;
        if (next === this.key) {
            return;
        }
        if (
            next === 'production' &&
            !confirm(
                `Você vai conectar em ${API_ENVIRONMENTS.production.label.toUpperCase()}. ` +
                    'Alterações feitas aqui afetam dados reais. Continuar?'
            )
        ) {
            select.value = this.key;
            return;
        }
        switchApiEnvironment(next);
    }
}
